/**
 * ╔═══════════════════════════════════════════════════════════════════╗
 * ║  SINGLE OWNER: Authentication & Session State                     ║
 * ║                                                                   ║
 * ║  This context is the ONLY authority for auth state.                ║
 * ║  - status: "loading" | "authed" | "unauthed"                      ║
 * ║  - isReady: true only after session hydration completes           ║
 * ║  - sessionExpired: indicates mid-use session loss                 ║
 * ║                                                                   ║
 * ║  NEVER check auth state outside this context.                     ║
 * ║  NEVER render protected content while status === "loading".       ║
 * ║  NEVER assume session exists until isReady === true.              ║
 * ╚═══════════════════════════════════════════════════════════════════╝
 */
import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { isActiveWorkflow, clearActiveWorkflow } from "@/lib/activeWorkflowGuard";
import { logger } from "@/lib/logger";
import { Capacitor } from "@capacitor/core";
import { logAuthEvent } from "@/lib/navigationLogger";
import { supabase } from "@/integrations/supabase/client";
import { suppressBiometricAutoLogin } from "@/lib/biometricAuth";
import type { Session } from "@supabase/supabase-js";

interface AuthState {
  status: "loading" | "authed" | "unauthed";
  session: Session | null;
  isReady: boolean;
  /** True when a previously-authed session expires/signs out mid-use */
  sessionExpired: boolean;
  dismissExpiry: () => void;
  /** Call before supabase.auth.signOut() to suppress the "session expired" banner */
  markManualLogout: () => void;
}

const AuthContext = createContext<AuthState>({
  status: "loading",
  session: null,
  isReady: false,
  sessionExpired: false,
  dismissExpiry: () => {},
  markManualLogout: () => {},
});

export const useAuth = () => useContext(AuthContext);

const AUTH_TIMEOUT_MS = 4000;
const NATIVE_AUTH_TIMEOUT_MS = 8000; // Extended: give native more time to restore session from storage
const INITIAL_SESSION_RETRY_DELAYS_MS = [200, 400];
const NATIVE_INITIAL_SESSION_RETRY_DELAYS_MS = [200, 400, 800, 1200];

/**
 * Check if Supabase has stored tokens in localStorage.
 * If tokens exist, the user was previously logged in and we should
 * NOT declare "unauthed" — instead keep waiting or preserve the session.
 */
const hasStoredAuthTokens = (): boolean => {
  try {
    const key = `sb-wfsbisepapnnnqyyekip-auth-token`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    // Check if there's a refresh token (indicates a real session was stored)
    return !!(parsed?.refresh_token || parsed?.access_token);
  } catch {
    return false;
  }
};

/**
 * Helper: returns true if an error is likely a network/connectivity issue
 * rather than a genuine auth rejection.
 */
const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  const msg = (error?.message || error?.toString() || "").toLowerCase();
  return (
    msg.includes("fetch") ||
    msg.includes("network") ||
    msg.includes("failed to fetch") ||
    msg.includes("load failed") ||
    msg.includes("networkerror") ||
    msg.includes("aborterror") ||
    msg.includes("timeout") ||
    msg.includes("econnrefused") ||
    msg.includes("err_internet_disconnected") ||
    msg.includes("the internet connection appears to be offline") ||
    msg.includes("the network connection was lost")
  );
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ status: "loading" | "authed" | "unauthed"; session: Session | null }>({
    status: "loading",
    session: null,
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const initialCheckDone = useRef(false);
  const wasAuthed = useRef(false);
  const manualLogout = useRef(false);
  /** Keep a reference to the last known good session so we can preserve it on network errors */
  const lastKnownSession = useRef<Session | null>(null);

  const markManualLogout = () => {
    manualLogout.current = true;
    suppressBiometricAutoLogin();
    // 1.72: a manual logout ends any saved workflow so it can never auto-restore
    // on the next sign-in. Only the guard is removed — drafts/photos are untouched.
    clearActiveWorkflow();
  };

  const dismissExpiry = () => setSessionExpired(false);

  useEffect(() => {
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const isNativePlatform = Capacitor.isNativePlatform();
    const authTimeoutMs = isNativePlatform ? NATIVE_AUTH_TIMEOUT_MS : AUTH_TIMEOUT_MS;

    // Timeout only for the initial load
    timeout = setTimeout(() => {
      if (!cancelled && !initialCheckDone.current) {
        logger.error(`[Auth] Session check timed out after ${authTimeoutMs}ms`);
        // If we had a previous session (e.g. app resumed with no signal), keep it
        if (lastKnownSession.current) {
          logger.debug("[Auth] Preserving last known session on timeout");
          setState({ status: "authed", session: lastKnownSession.current });
        } else if (isNativePlatform && hasStoredAuthTokens()) {
          // NATIVE FIX: Tokens exist in localStorage but getSession() is slow.
          // Do NOT declare "unauthed" — this causes the /auth flash that "kicks out" drivers.
          // Instead, try one more getSession() call. If it still fails, only THEN give up.
          logger.warn("[Auth] Timeout but stored tokens found — attempting final restore");
          supabase.auth.getSession().then(({ data: { session: lastChance } }) => {
            if (cancelled) return;
            if (lastChance) {
              logger.debug("[Auth] Final restore succeeded");
              setState({ status: "authed", session: lastChance });
              wasAuthed.current = true;
              lastKnownSession.current = lastChance;
            } else {
              logger.warn("[Auth] Final restore failed — going unauthed");
              setState({ status: "unauthed", session: null });
            }
            initialCheckDone.current = true;
          }).catch(() => {
            if (!cancelled) {
              logger.error("[Auth] Final restore threw — going unauthed");
              setState({ status: "unauthed", session: null });
              initialCheckDone.current = true;
            }
          });
          return; // Don't finalize yet — let the final restore attempt complete
        } else {
          setState({ status: "unauthed", session: null });
        }
        initialCheckDone.current = true;
      }
    }, authTimeoutMs);

    const init = async () => {
      let shouldFinalizeInitialCheck = true;

      try {
        let restoredSession: Session | null = null;
        let restoredError: any = null;
        const retryDelays = isNativePlatform
          ? NATIVE_INITIAL_SESSION_RETRY_DELAYS_MS
          : INITIAL_SESSION_RETRY_DELAYS_MS;

        for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
          const { data: { session }, error } = await supabase.auth.getSession();
          restoredSession = session;
          restoredError = error;

          if (error || session || initialCheckDone.current) {
            break;
          }

          if (attempt < retryDelays.length) {
            const delay = retryDelays[attempt];
            logger.debug(`[Auth] No session yet, retrying in ${delay}ms (attempt ${attempt + 1})`);
            await wait(delay);
          }
        }

        if (cancelled) return;

        if (initialCheckDone.current && lastKnownSession.current) {
          logger.debug("[Auth] Session already restored via auth event");
          setState({ status: "authed", session: lastKnownSession.current });
          return;
        }

        if (restoredError) {
          // CRITICAL: If it's a network error and we were previously authed,
          // keep the session alive instead of signing the user out
          if (isNetworkError(restoredError) && wasAuthed.current && lastKnownSession.current) {
            logger.warn("[Auth] Network error during getSession, preserving session");
            setState({ status: "authed", session: lastKnownSession.current });
          } else {
            logger.error("[Auth] getSession error");
            setState({ status: "unauthed", session: null });
          }
        } else if (restoredSession) {
          logger.debug("[Auth] Session restored");
          setState({ status: "authed", session: restoredSession });
          wasAuthed.current = true;
          lastKnownSession.current = restoredSession;
        } else if (isNativePlatform) {
          logger.debug("[Auth] Native: session not restored yet — waiting");
          shouldFinalizeInitialCheck = false;
          return;
        } else {
          logger.debug("[Auth] No session");
          setState({ status: "unauthed", session: null });
        }
      } catch (e: any) {
        logger.error("[Auth] Unexpected error");
        // On network errors, preserve existing session if we had one
        if (!cancelled) {
          if (isNetworkError(e) && wasAuthed.current && lastKnownSession.current) {
            logger.warn("[Auth] Network error caught, preserving session");
            setState({ status: "authed", session: lastKnownSession.current });
          } else {
            setState({ status: "unauthed", session: null });
          }
        }
      } finally {
        if (!cancelled && shouldFinalizeInitialCheck) {
          initialCheckDone.current = true;
          if (timeout) clearTimeout(timeout);
        }
      }
    };

    // Subscribe to auth changes — keeps state in sync after login/logout
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logAuthEvent(`onAuthStateChange: ${event}`);
      logger.debug("[Auth] onAuthStateChange:", event);

      if (event === "INITIAL_SESSION" && !initialCheckDone.current) {
        if (session) {
          logger.debug("[Auth] INITIAL_SESSION restored");
          setState({ status: "authed", session });
          wasAuthed.current = true;
          lastKnownSession.current = session;
          setSessionExpired(false);
          initialCheckDone.current = true;
          if (timeout) clearTimeout(timeout);
        } else {
          logger.debug("[Auth] INITIAL_SESSION without session — waiting for getSession()");
        }
        return;
      }

      // Ignore TOKEN_REFRESHED events that arrive with null session due to network issues
      if (!session && event === "TOKEN_REFRESHED") {
        logger.warn("[Auth] TOKEN_REFRESHED with null session, ignoring");
        return;
      }

      if (session) {
        setState({ status: "authed", session });
        wasAuthed.current = true;
        lastKnownSession.current = session;
        initialCheckDone.current = true;
        if (timeout) clearTimeout(timeout);
        setSessionExpired(false);
        // Re-evaluate debug mode now that we know the user's email
        import("@/lib/debugMode").then(({ bootstrapDebugMode: boot }) => {
          boot(session.user?.email);
        }).catch(() => {});

      } else {
        // Only transition to unauthed for definitive auth events (SIGNED_OUT)
        // For other events with null session while previously authed, preserve the session
        if (event === "SIGNED_OUT" || !wasAuthed.current) {
          setState({ status: "unauthed", session: null });
          // WORKFLOW GUARD: Don't flag session as "expired" during active appraisal —
          // this is just iOS killing/restarting the WebView during camera use.
          if (wasAuthed.current && !manualLogout.current && !isActiveWorkflow()) {
            setSessionExpired(true);
          }
          manualLogout.current = false;
          wasAuthed.current = false;
          lastKnownSession.current = null;
          initialCheckDone.current = true;
          if (timeout) clearTimeout(timeout);
        } else {
          // Previously authed, got null session from a non-SIGNED_OUT event
          // This is likely a network blip — keep the user logged in
          logger.warn("[Auth] Null session from event:", event, "— preserving authed state");
        }
      }
    });

    void init();

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, isReady: state.status !== "loading", sessionExpired, dismissExpiry, markManualLogout }}>
      {children}
    </AuthContext.Provider>
  );
}
